import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Users, TrendingUp, Globe, ArrowRight, Coins, Heart, Shield } from 'lucide-react';
import EquonomyLogo from '@/components/EquonomyLogo';

function Landing() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50">
      {/* Hero Section */}
      <div className="container mx-auto px-4 py-16">
        <div className="text-center mb-16">
          <div className="flex justify-center mb-6">
            <EquonomyLogo className="w-24 h-24" gradient={true} />
          </div>
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-4">
            Equonomy
          </h1>
          <p className="text-xl md:text-2xl text-gray-600 mb-8">
            Exchange Services & Goods Using Time, Build Community, Create Value
          </p>
          <div className="flex gap-4 justify-center flex-wrap">
            <Link to="/auth">
              <Button size="lg" className="text-lg px-8 py-6">
                Get Started
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>

        {/* What is Equonomy */}
        <Card className="mb-12 bg-white/80 backdrop-blur shadow-xl">
          <CardContent className="p-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
              <Users className="w-8 h-8 text-purple-600" />
              What is Equonomy?
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed">
              <p className="text-lg">
                Equonomy is a <strong>time banking platform</strong> where community members exchange <strong>services and goods</strong> using <strong>time as currency</strong>. 
                One hour of your time equals one Time Token - regardless of what you offer. Whether you're teaching a skill, sharing a tool, gardening, 
                offering a bicycle, or coding, your time is valued equally.
              </p>
              <p>
                Unlike traditional money-based systems, Equonomy creates a <strong>fair and inclusive economy</strong> where everyone's contribution 
                matters. No bank accounts needed, no fees, no inflation - just honest exchange of services, goods, skills, and time within your community.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Core Principles */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          <Card className="bg-white/80 backdrop-blur shadow-lg hover:shadow-xl transition-shadow">
            <CardContent className="p-6">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                <Shield className="w-6 h-6 text-blue-600" />
              </div>
              <h3 className="text-xl font-semibold mb-3 text-gray-900">Equal Value</h3>
              <p className="text-gray-600">
                Every hour is valued equally. A doctor's hour = a gardener's hour = your hour. True equality through time.
              </p>
            </CardContent>
          </Card>

          <Card className="bg-white/80 backdrop-blur shadow-lg hover:shadow-xl transition-shadow">
            <CardContent className="p-6">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
                <Users className="w-6 h-6 text-purple-600" />
              </div>
              <h3 className="text-xl font-semibold mb-3 text-gray-900">Community First</h3>
              <p className="text-gray-600">
                Build stronger local connections. Help your neighbors, earn time credits, and create a supportive community network.
              </p>
            </CardContent>
          </Card>

          <Card className="bg-white/80 backdrop-blur shadow-lg hover:shadow-xl transition-shadow">
            <CardContent className="p-6">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
                <Globe className="w-6 h-6 text-green-600" />
              </div>
              <h3 className="text-xl font-semibold mb-3 text-gray-900">Sustainable</h3>
              <p className="text-gray-600">
                No inflation, no scarcity of money. Time is abundant and renewable, making this economy inherently sustainable.
              </p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-yellow-50 to-orange-50 border-2 border-yellow-300 shadow-lg hover:shadow-xl transition-shadow">
            <CardContent className="p-6">
              <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center mb-4">
                <Heart className="w-6 h-6 text-orange-600 fill-orange-200" />
              </div>
              <h3 className="text-xl font-semibold mb-3 text-gray-900">Passion & Purpose</h3>
              <p className="text-gray-700">
                <strong>Beyond jobs for money.</strong> Every activity becomes an expression of passion. The reward is the 
                <strong className="text-orange-700"> happiness and fulfillment</strong> found in doing what you love while helping others. 
                Work transforms from obligation to <strong className="text-orange-700">joyful contribution</strong>.
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Advantages of Time Tokenization */}
        <Card className="mb-12 bg-gradient-to-br from-purple-50 to-blue-50 shadow-xl">
          <CardContent className="p-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
              <Coins className="w-8 h-8 text-purple-600" />
              Advantages of Time Tokenization
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-white text-sm">✓</span>
                  </div>
                  <div>
                    <strong className="text-gray-900">Fairness:</strong>
                    <span className="text-gray-700"> Time is equal for everyone. No discrimination based on profession or social status.</span>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-white text-sm">✓</span>
                  </div>
                  <div>
                    <strong className="text-gray-900">Inclusivity:</strong>
                    <span className="text-gray-700"> Everyone can participate regardless of financial status. Your time has value.</span>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-white text-sm">✓</span>
                  </div>
                  <div>
                    <strong className="text-gray-900">Transparency:</strong>
                    <span className="text-gray-700"> Clear transactions and fair exchanges. No hidden fees or intermediaries.</span>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-white text-sm">✓</span>
                  </div>
                  <div>
                    <strong className="text-gray-900">Economic Resilience:</strong>
                    <span className="text-gray-700"> Immune to inflation and market crashes. Time remains constant.</span>
                  </div>
                </div>
              </div>
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-white text-sm">✓</span>
                  </div>
                  <div>
                    <strong className="text-gray-900">Empowerment:</strong>
                    <span className="text-gray-700"> Control your wealth directly. Your time, your value, your freedom.</span>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-white text-sm">✓</span>
                  </div>
                  <div>
                    <strong className="text-gray-900">Community Building:</strong>
                    <span className="text-gray-700"> Fosters trust, cooperation, and stronger social connections.</span>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-white text-sm">✓</span>
                  </div>
                  <div>
                    <strong className="text-gray-900">Value Recognition:</strong>
                    <span className="text-gray-700"> Honors all forms of work - caregiving, teaching, art, and more.</span>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-white text-sm">✓</span>
                  </div>
                  <div>
                    <strong className="text-gray-900">Sustainability:</strong>
                    <span className="text-gray-700"> Built on renewable resources - the time we all share equally.</span>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Future Society & Human Value */}
        <Card className="mb-12 bg-gradient-to-br from-orange-50 to-yellow-50 shadow-xl border-2 border-orange-200">
          <CardContent className="p-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
              <Globe className="w-8 h-8 text-orange-600" />
              The Future We're Building For
            </h2>
            <div className="space-y-4 text-gray-700">
              <p className="text-lg font-semibold text-orange-900">
                As automation and AI transform the economy, many traditional jobs will disappear. But human connection cannot be automated.
              </p>
              <div className="bg-white/70 p-6 rounded-lg border-l-4 border-orange-500">
                <h3 className="text-xl font-bold text-gray-900 mb-3">The Automation Challenge</h3>
                <p className="mb-3">
                  In the coming decades, <strong>AI and robots will replace many jobs</strong>—from manufacturing to data analysis, from driving to basic services. 
                  This technological shift will leave millions seeking not just income, but <strong>purpose and meaning</strong> in their lives.
                </p>
                <p>
                  Traditional employment may become scarce, but the human need for <strong>connection, care, and community</strong> will remain constant—and may even grow stronger.
                </p>
              </div>
              <div className="bg-white/70 p-6 rounded-lg border-l-4 border-purple-500">
                <h3 className="text-xl font-bold text-gray-900 mb-3">What AI & Robots Cannot Replace</h3>
                <div className="space-y-2">
                  <p className="flex items-start gap-2">
                    <span className="text-purple-600 font-bold">•</span>
                    <span><strong>Caring for others:</strong> Elderly care, emotional support, companionship—the human touch that machines cannot replicate</span>
                  </p>
                  <p className="flex items-start gap-2">
                    <span className="text-purple-600 font-bold">•</span>
                    <span><strong>Teaching and mentoring:</strong> Passing wisdom, inspiring growth, providing personalized guidance</span>
                  </p>
                  <p className="flex items-start gap-2">
                    <span className="text-purple-600 font-bold">•</span>
                    <span><strong>Creative collaboration:</strong> Art, music, storytelling—human expression and shared creativity</span>
                  </p>
                  <p className="flex items-start gap-2">
                    <span className="text-purple-600 font-bold">•</span>
                    <span><strong>Community building:</strong> Organizing, volunteering, creating spaces where people belong</span>
                  </p>
                  <p className="flex items-start gap-2">
                    <span className="text-purple-600 font-bold">•</span>
                    <span><strong>Emotional labor:</strong> Listening, empathizing, being present—the essence of humanity</span>
                  </p>
                </div>
              </div>
              <div className="bg-gradient-to-r from-purple-100 to-blue-100 p-6 rounded-lg">
                <h3 className="text-xl font-bold text-gray-900 mb-3">Equonomy's Role in This Future</h3>
                <p className="mb-3">
                  When traditional jobs disappear, <strong>Equonomy provides purpose through contribution</strong>. Every hour you dedicate to helping others, 
                  caring for your community, or sharing your skills has tangible value—not measured in money that may be scarce, but in time that remains abundant.
                </p>
                <p className="mb-3">
                  In a world where <strong>robots produce goods but cannot provide genuine care</strong>, human-to-human services become more precious. 
                  Equonomy ensures these irreplaceable human contributions are recognized, valued, and exchanged fairly.
                </p>
                <p className="font-semibold text-purple-900">
                  This isn't just an alternative economy—it's a lifeline for human dignity and purpose in an automated future. 
                  Your time spent caring, teaching, creating, and connecting will always have value here.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Future Development */}
        <Card className="mb-12 bg-white/80 backdrop-blur shadow-xl">
          <CardContent className="p-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
              <TrendingUp className="w-8 h-8 text-purple-600" />
              Future Development
            </h2>
            <div className="space-y-4 text-gray-700">
              <p className="text-lg">
                <strong>Phase 1 (Current):</strong> Local community time banking with peer-to-peer service exchange, 
                location-based marketplace, and instant messaging.
              </p>
              <p className="text-lg">
                <strong>Phase 2 (Planned):</strong> Blockchain integration for decentralized, transparent time token management. 
                Implementation of smart contracts for automated escrow and dispute resolution.
              </p>
              <p className="text-lg">
                <strong>Phase 3 (Vision):</strong> Wallet-based authentication, cross-community exchanges, and integration with 
                global time banking networks. Building a sustainable digital economy where wealth returns to creators.
              </p>
              <p className="text-base text-gray-600 italic mt-4">
                Our goal: Create an inclusive, secure, and practical ecosystem that values everyone's time equally and empowers 
                communities worldwide to exchange services, goods, and skills without traditional monetary barriers.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* CTA Section */}
        <div className="text-center bg-gradient-to-r from-purple-600 to-blue-600 rounded-2xl p-12 text-white shadow-2xl">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Ready to Join the Time Revolution?
          </h2>
          <p className="text-xl mb-8 text-purple-100">
            Start exchanging services and goods using time credits today. Build a stronger community tomorrow.
          </p>
          <Link to="/auth">
            <Button size="lg" variant="secondary" className="text-lg px-8 py-6">
              Create Your Account
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-300 py-8 mt-16">
        <div className="container mx-auto px-4 text-center">
          <p className="text-sm">
            © 2024 Equonomy. Building a fairer economy, one hour at a time.
          </p>
          <p className="text-xs mt-2 text-gray-500">
            Inspired by global time banking movements and blockchain innovation
          </p>
        </div>
      </footer>
    </div>
  );
}

export default Landing;
